import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { InsertionOrder } from '../../../redux/billing/billing.types';
import { InsertionOrderTable } from './insertion-order-table';
import { DocumentPreviewModal } from './document-preview-modal';
import { useInsertionOrderForm } from '../hooks/use-insertion-order-form';

interface AutocompleteInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  suggestions?: string[];
  className?: string;
  required?: boolean;
  disabled?: boolean;
  type?: string;
}

const AutocompleteInput = ({
  id,
  value,
  onChange,
  placeholder,
  suggestions = [],
  className,
  required,
  disabled,
  type = 'text',
}: AutocompleteInputProps) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!value || !suggestions.length) {
      setFilteredSuggestions([]);
      return;
    }

    const filtered = suggestions
      .filter(suggestion =>
        suggestion &&
        suggestion.toLowerCase().includes(value.toLowerCase()) &&
        suggestion.toLowerCase() !== value.toLowerCase()
      )
      .slice(0, 5); // Limit to 5 suggestions

    setFilteredSuggestions(filtered);
  }, [value, suggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowSuggestions(newValue.length > 0);
  };

  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    if (value.length > 0 && filteredSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        id={id}
        type={type}
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        className={className}
        required={required}
        disabled={disabled}
      />
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto mt-1"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={index}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface InsertionOrderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'create' | 'edit' | 'view';
  initialData?: InsertionOrder;
  onSuccess?: () => void;
}

export const InsertionOrderModal = ({
  open,
  onOpenChange,
  mode = 'create',
  initialData,
  onSuccess,
}: InsertionOrderModalProps) => {
  const {
    formData,
    budgetData,
    setBudgetData,
    validationErrors,
    isPreviewModalOpen,
    setIsPreviewModalOpen,
    previewData,
    isGenerating,
    isReadOnly,
    isSaving,
    isSending,
    isLoadingPreview,
    handleInputChange,
    handleSave,
    handlePreviewAndSend,
    handleApproveAndSend,
    handleBackToEdit,
    handleViewDocument,
    getTitle,
    getSuggestions,
  } = useInsertionOrderForm({
    open,
    onOpenChange,
    mode,
    initialData,
    onSuccess,
    provider: 'docusign',
  });

  const getInputClassName = (field: string) => {
    const baseClass = "h-8 text-sm";
    const hasError = validationErrors[field];
    return hasError ? `${baseClass} border-red-500 focus:border-red-500 focus:ring-red-500` : baseClass;
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
      >
        <DialogContent className="sm:max-w-[900px] bg-white max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {getTitle()}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Main Layout: 2 Columns */}
            <div className="flex flex-col md:flex-row gap-4">
              {/* Column 1: Company Info (1/4 width) */}
              <div className="md:w-1/4 space-y-2">
                <div className="space-y-1">
                  <label
                    htmlFor="company_name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Company Name *
                  </label>
                  <AutocompleteInput
                    id="company_name"
                    value={formData.company_name || ''}
                    onChange={(value) => handleInputChange('company_name', value)}
                    placeholder="Company name"
                    suggestions={getSuggestions('company_name')}
                    className={getInputClassName('company_name')}
                    required
                    disabled={isReadOnly}
                  />
                  {validationErrors.company_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {validationErrors.company_name}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <label
                    htmlFor="advertiser_name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Advertiser Name *
                  </label>
                  <AutocompleteInput
                    id="advertiser_name"
                    value={formData.advertiser_name}
                    onChange={(value) => handleInputChange('advertiser_name', value)}
                    placeholder="Advertiser name"
                    suggestions={getSuggestions('advertiser_name')}
                    className={getInputClassName('advertiser_name')}
                    required
                    disabled={isReadOnly}
                  />
                  {validationErrors.advertiser_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {validationErrors.advertiser_name}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <label
                    htmlFor="campaign_name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Campaign Name *
                  </label>
                  <AutocompleteInput
                    id="campaign_name"
                    value={formData.campaign_name}
                    onChange={(value) => handleInputChange('campaign_name', value)}
                    placeholder="Campaign name"
                    suggestions={getSuggestions('campaign_name')}
                    className={getInputClassName('campaign_name')}
                    required
                    disabled={isReadOnly}
                  />
                  {validationErrors.campaign_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {validationErrors.campaign_name}
                    </p>
                  )}
                </div>
              </div>

              {/* Vertical Divider */}
              <div className="hidden md:block w-px bg-gray-200 mx-1" />

              {/* Column 2: Billing Details (3/4 width) */}
              <div className="md:w-3/4 space-y-2 flex-col">
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  {/* Sub-Column 1: Dates */}
                  <div className="flex flex-col gap-2 flex-grow md:max-w-[150px]">
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor="start_date"
                        className="text-sm font-medium text-gray-700"
                      >
                        Start Date *
                      </label>
                      <Input
                        id="start_date"
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => handleInputChange('start_date', e.target.value)}
                        className={getInputClassName('start_date')}
                        required
                        disabled={isReadOnly}
                      />
                      {validationErrors.start_date && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors.start_date}
                        </p>
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor="end_date"
                        className="text-sm font-medium text-gray-700"
                      >
                        End Date *
                      </label>
                      <Input
                        id="end_date"
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => handleInputChange('end_date', e.target.value)}
                        className={getInputClassName('end_date')}
                        required
                        disabled={isReadOnly}
                      />
                      {validationErrors.end_date && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors.end_date}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Vertical Divider */}
                  <div className="hidden md:block w-px bg-gray-200 mx-1" />

                  {/* Sub-Column 2: Client Details */}
                  <div className="flex flex-col gap-2 flex-grow">
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor="client_name"
                        className="text-sm font-medium text-gray-700"
                      >
                        Client Name *
                      </label>
                      <AutocompleteInput
                        id="client_name"
                        value={formData.client_name}
                        onChange={(value) => handleInputChange('client_name', value)}
                        placeholder="Client name"
                        suggestions={getSuggestions('client_name')}
                        className={getInputClassName('client_name')}
                        required
                        disabled={isReadOnly}
                      />
                      {validationErrors.client_name && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors.client_name}
                        </p>
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor="client_email"
                        className="text-sm font-medium text-gray-700"
                      >
                        Client Email *
                      </label>
                      <AutocompleteInput
                        id="client_email"
                        type="email"
                        value={formData.client_email}
                        onChange={(value) => handleInputChange('client_email', value)}
                        placeholder="client@example.com"
                        suggestions={getSuggestions('client_email')}
                        className={getInputClassName('client_email')}
                        required
                        disabled={isReadOnly}
                      />
                      {validationErrors.client_email && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors.client_email}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Vertical Divider */}
                  <div className="hidden md:block w-px bg-gray-200 mx-1" />

                  {/* Sub-Column 3: Agent Details */}
                  <div className="flex flex-col gap-2 flex-grow">
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor="agent_name"
                        className="text-sm font-medium text-gray-700"
                      >
                        Internal Name *
                      </label>
                      <AutocompleteInput
                        id="agent_name"
                        value={formData.agent_name}
                        onChange={(value) => handleInputChange('agent_name', value)}
                        placeholder="Agent name"
                        suggestions={getSuggestions('agent_name')}
                        className={getInputClassName('agent_name')}
                        required
                        disabled={isReadOnly}
                      />
                      {validationErrors.agent_name && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors.agent_name}
                        </p>
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor="agent_email"
                        className="text-sm font-medium text-gray-700"
                      >
                        Internal Email *
                      </label>
                      <AutocompleteInput
                        id="agent_email"
                        type="email"
                        value={formData.agent_email}
                        onChange={(value) => handleInputChange('agent_email', value)}
                        placeholder="agent@example.com"
                        suggestions={getSuggestions('agent_email')}
                        className={getInputClassName('agent_email')}
                        required
                        disabled={isReadOnly}
                      />
                      {validationErrors.agent_email && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors.agent_email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* CC Emails */}
                <div className="space-y-1 !mt-3">
                  <div className="flex flex-col md:flex-row md:items-center md:gap-2">
                    <label
                      htmlFor="cc_emails"
                      className="text-sm font-medium text-gray-700"
                    >
                      CC Emails (comma-separated)
                    </label>
                    <span className="text-xs text-gray-500 italic">
                      Include everyone who should receive status updates
                    </span>
                  </div>
                  <Input
                    id="cc_emails"
                    value={formData.cc_emails}
                    onChange={(e) => handleInputChange('cc_emails', e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                    className={getInputClassName('cc_emails')}
                    disabled={isReadOnly}
                  />
                  {validationErrors.cc_emails && (
                    <p className="text-red-500 text-xs mt-1">
                      {validationErrors.cc_emails}
                    </p>
                  )}
                </div>

                {/* Additional Options */}
                <div className="space-y-1 !mt-3">
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                      Additional Options:
                    </label>

                    <select
                      id="logo"
                      value={formData.logo}
                      onChange={(e) => handleInputChange('logo', e.target.value)}
                      disabled={isReadOnly}
                      className="h-8 text-sm border border-gray-300 rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="invisibly">
                        Invisibly
                      </option>
                      <option value="campaign_insights">
                        Campaign Insights
                      </option>
                    </select>

                    <div className="flex items-center space-x-2 ml-2">
                      <input
                        type="checkbox"
                        id="prebill"
                        checked={formData.prebill}
                        onChange={(e) => handleInputChange('prebill', e.target.checked)}
                        disabled={isReadOnly}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label
                        htmlFor="prebill"
                        className="text-sm text-gray-700 cursor-pointer whitespace-nowrap"
                      >
                        Prebill
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="include_sla"
                        checked={formData.include_sla}
                        onChange={(e) => handleInputChange('include_sla', e.target.checked)}
                        disabled={isReadOnly}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label
                        htmlFor="include_sla"
                        className="text-sm text-gray-700 cursor-pointer whitespace-nowrap"
                      >
                        Include SLA
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-2">
              <InsertionOrderTable
                initialData={budgetData}
                onChange={setBudgetData}
                disabled={isReadOnly}
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </Button>
            {isReadOnly && initialData?.document_url && (
              <Button
                className="bg-green-1000 text-white hover:bg-green-1000/90"
                onClick={handleViewDocument}
              >
                View Document
              </Button>
            )}
            {!isReadOnly && (
              <>
                <Button
                  variant="outline"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  className="bg-green-1000 text-white hover:bg-green-1000/90"
                  onClick={handlePreviewAndSend}
                  disabled={isSaving || isLoadingPreview || isGenerating}
                >
                  {(isSaving || isLoadingPreview || isGenerating) ? 'Generating...' : 'Preview & Send'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentPreviewModal
        open={isPreviewModalOpen}
        onOpenChange={setIsPreviewModalOpen}
        documentUrl={previewData?.document_url || null}
        onApproveAndSend={handleApproveAndSend}
        onBackToEdit={handleBackToEdit}
        isLoading={isSending}
        viewOnly={isReadOnly}
      />
    </>
  );
};
